import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API_URL as API } from "../config";
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("curious123_access_token");
    const headers = { ...options.headers };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const newToken = localStorage.getItem("curious123_access_token");
        headers["Authorization"] = `Bearer ${newToken}`;
        return fetch(url, { ...options, headers });
      } else {
        setUser(null);
        localStorage.removeItem("curious123_access_token");
        localStorage.removeItem("curious123_refresh_token");
      }
    }
    return res;
  }, []);

  const tryRefresh = async () => {
    const refreshToken = localStorage.getItem("curious123_refresh_token");
    if (!refreshToken) return false;
    try {
      const formData = new URLSearchParams();
      formData.append("refresh_token", refreshToken);
      const res = await fetch(`${API}/api/auth/refresh`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("curious123_access_token", data.access_token);
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
        return true;
      }
    } catch {}
    return false;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("curious123_access_token");
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (res.ok) {
          setUser(await res.json());
        } else {
          const refreshed = await tryRefresh();
          if (!refreshed) {
            localStorage.removeItem("curious123_access_token");
            localStorage.removeItem("curious123_refresh_token");
          }
        }
      } catch {
        const stored = localStorage.getItem("curious123_user");
        if (stored) setUser(JSON.parse(stored));
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) localStorage.setItem("curious123_user", JSON.stringify(user));
    else localStorage.removeItem("curious123_user");
  }, [user]);

  const login = async (email, password) => {
    let res;
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("password", password);
      res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        body: formData,
      });
    } catch (err) {
      throw new Error("Network error. Please check your connection and try again.");
    }
    if (!res.ok) {
      let detail = "Login failed";
      try {
        const data = await res.json();
        detail = typeof data.detail === "string" ? data.detail : "Invalid credentials";
      } catch {}
      throw new Error(detail);
    }
    const data = await res.json();
    localStorage.setItem("curious123_access_token", data.access_token);
    localStorage.setItem("curious123_refresh_token", data.refresh_token);
    const userData = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("curious123_access_token");
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        headers: token ? { "Authorization": `Bearer ${token}` } : {},
      });
    } catch {}
    localStorage.removeItem("curious123_access_token");
    localStorage.removeItem("curious123_refresh_token");
    localStorage.removeItem("curious123_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
